// The decision panel beside the script (E31-S6-T3; ADR-033; mockups/script-review.html, "the decision
// panel"). What it offers follows the version's state and the viewer:
//
//   in review   Approve (after the box is ticked) and Send back with a note; held while an open
//               contradiction is linked to the title, with links to it in the source issues queue
//   approved    rendering: nothing to do; rendered: the player, Publish to learners and Send back;
//               failed: the reason where the API gives it, and Send back
//   draft       the note it was sent back with, if any
//   published   live
//
// Whoever created, submitted or edited the version sees no Approve or Publish control: the panel reads
// that from the version's own history, the way the API decides it (ADR-033 decisions 2 and 4). The API
// decides again on every act, and its refusal is shown in the catalogue's words. Every act is one POST.
import { Button, cn } from "@waypoint/surface";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { Link } from "wouter";
import { ConflictError, ForbiddenError, ValidationError } from "../../api/errors.js";
import { t } from "../../i18n/t.js";
import { FieldError, Label, TextArea } from "../../ui/form.js";
import { isDrafter, stageOf, trailOf, type VersionDetail } from "../../versions/model.js";
import {
  useRequestRender,
  useReviewAct,
  type Acted,
  type ReviewAct,
} from "../../versions/queries.js";
import { formatWhen } from "./issue-format.js";
import { ReviewPlayer } from "./intro-card.js";
import { mmss } from "./plan-model.js";
import { failureWords, trailWords, type TitleInfo } from "./script-format.js";

const OVERLINE = "text-11 font-semibold tracking-[0.08em] text-wp-text-3 uppercase";
const NOTE = "m-0 text-13 text-wp-text-2";
/** aria-disabled keeps a control focusable and announced while it cannot act yet (the mockup's way). */
const HELD_BACK = "aria-disabled:cursor-not-allowed aria-disabled:opacity-45";

export function Callout({
  tone,
  title,
  children,
}: {
  tone: "plain" | "caution" | "ask";
  title?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "rounded-8 border px-3 py-2.5 text-13",
        tone === "plain" && "border-wp-line-strong bg-wp-bg/58 text-wp-text-2",
        tone === "caution" && "border-caution/48 bg-caution/11 text-wp-text",
        tone === "ask" && "border-wp-accent/44 bg-wp-accent/13 text-wp-text",
      )}
    >
      {title === undefined ? null : <b className="mb-0.5 block text-wp-text">{title}</b>}
      {children}
    </div>
  );
}

/** What the API's refusal of an act means on this screen. */
export function refusalLine(err: unknown, act: ReviewAct["act"]): string {
  if (err instanceof ForbiddenError)
    return t(
      act === "approve"
        ? "review.refused.approve"
        : act === "publish"
          ? "review.refused.publish"
          : "review.refused.sendBack",
    );
  if (err instanceof ConflictError) {
    if (err.fields.includes("issues")) return t("review.refused.issues");
    if (err.fields.includes("render.sha256")) return t("review.refused.renderHash");
    if (err.fields.includes("render")) return t("review.refused.render");
    return t("review.refused.conflict");
  }
  if (err instanceof ValidationError && err.source === "request") {
    if (act === "send-back") return t("review.refused.note");
    const code = err.refusals[0]?.code;
    if (code === "render.contradiction-open") return t("review.refused.contradiction");
    if (code === "render.plan-over-budget") return t("review.refused.overBudget");
    return t("review.refused.renderOther");
  }
  return t("review.refused.failed");
}

/** What the render route's refusal means here: the five codes it names, then the connection's answer. */
export function renderAgainLine(err: unknown): string {
  if (err instanceof ForbiddenError) return t("review.refused.renderAgain");
  if (err instanceof ValidationError && err.source === "request") {
    switch (err.refusals[0]?.code) {
      case "render.not-approved":
        return t("review.refused.renderNotApproved");
      case "render.contradiction-open":
        return t("review.refused.contradictionAgain");
      case "render.plan-over-budget":
        return t("review.refused.overBudgetAgain");
      case "render.title-unknown":
        return t("review.refused.titleUnknown");
      case "render.theme-unknown":
        return t("review.refused.themeUnknown");
    }
  }
  return t("review.refused.failed");
}

function Trail({ version, userId }: { version: VersionDetail; userId: string }) {
  return (
    <div>
      <div className={cn(OVERLINE, "mb-1.5")}>{t("review.history")}</div>
      <ul className="m-0 grid list-none gap-1.5 p-0 text-13 text-wp-text-2">
        {trailOf(version).map((entry) => (
          <li key={`${entry.kind}-${entry.at}`} className="grid grid-cols-[auto_1fr] gap-2">
            <time dateTime={entry.at} className="text-12 text-wp-text-3">
              {formatWhen(entry.at)}
            </time>
            <span className="min-w-0 break-words">{trailWords(entry, userId)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return (
    <h2 id="decision-heading" tabIndex={-1} className="m-0 text-16 font-semibold outline-none">
      {children}
    </h2>
  );
}

interface Props {
  readonly version: VersionDetail;
  readonly info: TitleInfo;
  readonly userId: string;
  /** Called after the API accepted an act; the page announces it. */
  readonly onActed: (acted: Acted) => void;
}

export function Decision({ version, info, userId, onActed }: Props) {
  const ids = useId();
  const act = useReviewAct(version.id ?? "", version.titleId);
  const again = useRequestRender(version.id ?? "", version.titleId);
  const [ticked, setTicked] = useState(false);
  const [teaches, setTeaches] = useState<boolean | undefined>(undefined);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState<string | undefined>(undefined);
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const noteRef = useRef<HTMLTextAreaElement>(null);

  const stage = stageOf(version, info.held);
  const drafter = isDrafter(version, userId);
  const first = version.number === 1;
  const busy = act.isPending;

  const run = async (request: ReviewAct) => {
    setProblem(undefined);
    try {
      const done = await act.mutateAsync(request);
      onActed(done);
    } catch (err) {
      // A refused note is the note field's error; anything else is the panel's.
      if (err instanceof ValidationError && request.act === "send-back") {
        setNoteError(refusalLine(err, request.act));
        noteRef.current?.focus();
        return;
      }
      setProblem(refusalLine(err, request.act));
    }
  };

  // The mutation's pending flag reaches the button a tick late; the ref shuts a double click out at once.
  const asking = useRef(false);
  const renderAgain = async () => {
    if (asking.current) return;
    asking.current = true;
    setProblem(undefined);
    try {
      await again.mutateAsync();
    } catch (err) {
      setProblem(renderAgainLine(err));
    } finally {
      asking.current = false;
    }
  };

  const openSendBack = () => {
    setSending(true);
    setProblem(undefined);
  };
  // The note field takes the keyboard as it opens (the mockup's $("note").focus()).
  useEffect(() => {
    if (sending) noteRef.current?.focus();
  }, [sending]);

  const sendBack = () => {
    if (note.trim() === "") {
      setNoteError(t("review.note.required"));
      noteRef.current?.focus();
      return;
    }
    setNoteError(undefined);
    void run({ act: "send-back", note: note.trim() });
  };

  const sendBackForm = sending ? (
    <>
      <div>
        <Label htmlFor={`${ids}-note`}>{t("review.note.label")}</Label>
        <TextArea
          ref={noteRef}
          id={`${ids}-note`}
          value={note}
          placeholder={t("review.note.placeholder")}
          aria-invalid={noteError === undefined ? undefined : true}
          aria-describedby={`${ids}-note-error`}
          onChange={(e) => {
            setNote(e.target.value);
            if (e.target.value.trim() !== "") setNoteError(undefined);
          }}
        />
        <FieldError id={`${ids}-note-error`}>{noteError}</FieldError>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          className={cn(
            "border-stop/62 text-stop-soft hover:border-stop hover:bg-stop/14",
            HELD_BACK,
          )}
          aria-disabled={note.trim() === "" || busy}
          onClick={() => {
            if (!busy) sendBack();
          }}
        >
          {t("review.note.go")}
        </Button>
        <Button
          variant="quiet"
          onClick={() => {
            setSending(false);
            setNote("");
            setNoteError(undefined);
          }}
        >
          {t("review.note.cancel")}
        </Button>
      </div>
    </>
  ) : null;

  const refusal =
    problem === undefined ? null : (
      <p role="alert" className="m-0 text-13 text-stop-soft">
        {problem}
      </p>
    );

  const trail = <Trail version={version} userId={userId} />;

  if (version.state === "in-review") {
    if (drafter)
      return (
        <>
          <Heading>{t("review.decide.waiting")}</Heading>
          <Callout tone="plain" title={t("review.decide.cantApprove")}>
            {t("review.decide.cantApproveLine")}
          </Callout>
          {trail}
        </>
      );
    const held = stage === "held";
    const ready = ticked && (first || teaches !== undefined) && !held;
    return (
      <>
        <Heading>{t("review.decide.yours")}</Heading>
        {held ? (
          <Callout tone="caution" title={t("review.decide.held")}>
            {t("review.decide.heldBefore")}{" "}
            {info.held.map((id, i) => (
              <span key={id}>
                {i > 0 ? ", " : null}
                <Link
                  href={`/issues/${encodeURIComponent(info.packId)}#${encodeURIComponent(id)}`}
                  aria-label={t("review.decide.heldLink", { id })}
                  className="font-semibold text-wp-accent underline underline-offset-2"
                >
                  {id}
                </Link>
              </span>
            ))}
            . {t("review.decide.heldAfter")}
          </Callout>
        ) : (
          <>
            <p className={NOTE}>
              {t("review.decide.filming", { time: mmss(info.measured ?? info.planned) })}
            </p>
            {first ? null : (
              <fieldset className="m-0 grid gap-1.5 border-0 p-0 text-13">
                <legend className="mb-1 text-13 font-semibold">{t("review.decide.teaches")}</legend>
                {([true, false] as const).map((answer) => (
                  <label key={String(answer)} className="flex items-start gap-2">
                    <input
                      type="radio"
                      name={`${ids}-teaches`}
                      checked={teaches === answer}
                      onChange={() => {
                        setTeaches(answer);
                      }}
                      className="mt-0.75 accent-wp-accent"
                    />
                    {t(answer ? "review.decide.teachesYes" : "review.decide.teachesNo")}
                  </label>
                ))}
              </fieldset>
            )}
            <label className="flex items-start gap-2 text-13">
              <input
                type="checkbox"
                checked={ticked}
                onChange={(e) => {
                  setTicked(e.target.checked);
                }}
                className="mt-0.75 accent-wp-accent"
              />
              {t("review.decide.tick")}
            </label>
          </>
        )}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            className={HELD_BACK}
            aria-disabled={!ready || busy}
            onClick={() => {
              if (ready && !busy)
                void run({ act: "approve", teachesChanged: first || teaches === true });
            }}
          >
            {busy && act.variables.act === "approve" ? t("review.approving") : t("review.approve")}
          </Button>
          <Button aria-expanded={sending} onClick={openSendBack}>
            {t("review.sendBack")}
          </Button>
        </div>
        {refusal}
        {sendBackForm}
        {trail}
      </>
    );
  }

  if (version.state === "approved") {
    if (stage === "rendering")
      return (
        <>
          <Heading>{t("review.rendering.title")}</Heading>
          <div aria-hidden className="h-1.5 overflow-hidden rounded-pill bg-wp-bg/58">
            <i className="block h-full w-2/5 bg-wp-accent" />
          </div>
          <p className={NOTE}>{t("review.rendering.line")}</p>
          {trail}
        </>
      );
    if (stage === "ready" && drafter)
      return (
        <>
          <Heading>{t("review.ready.drafterTitle")}</Heading>
          <p className={NOTE}>{t("review.ready.drafterLine")}</p>
          {trail}
        </>
      );
    const duration = version.render?.durationSeconds;
    return (
      <>
        {stage === "ready" ? (
          <>
            <Heading>{t("review.ready.title")}</Heading>
            <ReviewPlayer key={version.id} version={version} info={info} duration={duration} />
            <p className={NOTE}>{t("review.ready.line")}</p>
          </>
        ) : (
          <>
            <Heading>{t("review.videoFailed.title")}</Heading>
            <Callout tone="caution">
              {t("review.videoFailed.line")}
              {version.renderFailure === undefined ? null : (
                <span className="mt-1 block">
                  {t("review.videoFailed.reason", {
                    reason: failureWords(version.renderFailure.reason),
                  })}
                </span>
              )}
            </Callout>
          </>
        )}
        <div className="flex flex-wrap gap-2">
          {stage === "failed" && !drafter && version.renderFailure !== undefined ? (
            <Button
              variant="primary"
              className={HELD_BACK}
              aria-disabled={again.isPending}
              onClick={() => {
                if (!again.isPending) void renderAgain();
              }}
            >
              {again.isPending ? t("review.renderAgain.sending") : t("review.renderAgain")}
            </Button>
          ) : null}
          {stage === "ready" && !drafter ? (
            <Button
              variant="primary"
              className={HELD_BACK}
              aria-disabled={busy}
              onClick={() => {
                if (!busy) void run({ act: "publish" });
              }}
            >
              {busy && act.variables.act === "publish"
                ? t("review.publishing")
                : t("review.publish")}
            </Button>
          ) : null}
          <Button aria-expanded={sending} onClick={openSendBack}>
            {t("review.sendBack")}
          </Button>
        </div>
        {refusal}
        {sendBackForm}
        {trail}
      </>
    );
  }

  if (version.state === "draft") {
    const sentBack = version.sentBack;
    return (
      <>
        <Heading>
          {t(sentBack === undefined ? "review.draft.title" : "review.draft.sentBack")}
        </Heading>
        {sentBack === undefined ? (
          <p className={NOTE}>{t("review.draft.notSubmitted")}</p>
        ) : (
          <>
            <Callout
              tone="ask"
              title={t(sentBack.by === userId ? "review.draft.yourNote" : "review.draft.theNote")}
            >
              {sentBack.reason}
            </Callout>
            <p className={NOTE}>{t("review.draft.revise")}</p>
          </>
        )}
        {trail}
      </>
    );
  }

  const closed = version.state === "published" ? "live" : version.state;
  return (
    <>
      <Heading>{t(`review.${closed}.title`)}</Heading>
      <p className={NOTE}>{t(`review.${closed}.line`)}</p>
      {trail}
    </>
  );
}
